import { t } from '../i18n/catalog'
import { ApiError } from './api'

/**
 * 운영 UI에서 사용할 사용자용 오류 문구를 한 곳에서 관리합니다.
 *
 * 원칙:
 * - backend/downstream의 raw message, HTTP status, 내부 API명/서비스명을 그대로 노출하지 않습니다.
 * - 사용자가 바로 조치할 수 있는 알려진 오류는 code 기준으로 자연스러운 문구로 변환합니다.
 * - 문의 추적이 필요한 장애만 안정적인 사용자용 오류코드를 마지막에 붙입니다.
 * - backend 자연어 message는 사용자 메시지 계약으로 사용하지 않습니다.
 * - 개발자는 ApiError의 code/status/supportCode를 진단에 사용할 수 있지만,
 *   이 함수는 운영 사용자에게 backend 원문을 그대로 보여주지 않습니다.
 */
const ADMIN_KOREAN_MESSAGES: Record<string, string> = {
  SEGNO_ADMIN_REASON_REQUIRED: '운영 사유를 입력해 주세요.',
  SEGNO_ADMIN_SHARE_NOT_HELD: '운영자에 의해 중지된 공유가 아닙니다.',
  COPYRIGHT_REPORT_HOLD: '저작권 신고 확인이 남아 있어 공유를 복구할 수 없습니다.',
  SEGNO_ADMIN_SONG_NOT_BLINDED: '블라인드된 곡이 아닙니다.',
  SEGNO_ADMIN_SONG_NOT_FOUND: '곡을 찾을 수 없습니다.',
  SEGNO_ADMIN_USER_NOT_FOUND: '사용자를 찾을 수 없습니다.',
}

function userMessageKey(code: string): string {
  return `errors.${code}`
}


const SUPPORT_CODE_BY_INTERNAL_PREFIX: Array<[string, string]> = [
  ['CENTRAL_MEMBER_', 'SEGNO-ACCOUNT-001'],
  ['MEDIA_', 'SEGNO-AUDIO-001'],
  ['CSRF_', 'SEGNO-SECURITY-001'],
  ['SHARE_ID_GENERATION_', 'SEGNO-SHARE-001'],
  ['PLAN_POLICY_', 'SEGNO-PLAN-001'],
]

function supportCodeFor(error: ApiError): string | null {
  if (error.supportCode) return error.supportCode

  for (const [prefix, supportCode] of SUPPORT_CODE_BY_INTERNAL_PREFIX) {
    if (error.code.startsWith(prefix)) return supportCode
  }

  if (error.status >= 500) return 'SEGNO-SERVER-001'
  return null
}

export function userMessageForCode(code: string, fallback: string): string {
  return ADMIN_KOREAN_MESSAGES[code] ?? t(userMessageKey(code), {}, fallback)
}

export function userMessageForError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const base = userMessageForCode(error.code, fallback)
    const supportCode = supportCodeFor(error)
    return supportCode
      ? `${base} ${t('common.errorCode', { code: supportCode }, `오류 코드: ${supportCode}`)}`
      : base
  }

  // fetch 네트워크 실패처럼 서버 응답 자체를 받지 못한 경우에도 기술 원문은 노출하지 않습니다.
  if (error instanceof TypeError) {
    return `${fallback} ${t('common.errorCode', { code: 'SEGNO-NET-001' }, '오류 코드: SEGNO-NET-001')}`
  }

  return fallback
}
